/**
 * Pending-attachment state for the Flux Agent composer. Files picked or dropped are size-checked
 * against `limits.maxFileBytes`, read as base64, and uploaded immediately; ready uploads contribute
 * their ids to the next message. Server rejections (413 too large, 415 unsupported type) are shown
 * on the chip with the API's own message.
 *
 * @module apps/web/src/components/flux-agent/useFluxAgentAttachments
 */

import { useCallback, useRef, useState } from 'react';
import { fluxAgentApi, type FluxAgentFile } from '@/lib/fluxAgentApi';
import { blobToBase64 } from '@/lib/fluxAgentVoice';
import { formatBytes, guessMimeType } from '@/lib/fluxAgentFiles';

/** One composer attachment chip. */
export interface PendingAttachment {
  /** Client-side key, stable across the upload. */
  key: string;
  /** File name shown on the chip and sent with the upload. */
  name: string;
  /** Local file size in bytes, checked against the server limit before upload. */
  sizeBytes: number;
  /** Upload progress; only `ready` chips contribute ids to the next message. */
  status: 'uploading' | 'ready' | 'error';
  /** Server record once uploaded. */
  file?: FluxAgentFile;
  /** Why the upload failed. */
  error?: string;
}

/** State and operations returned by {@link useFluxAgentAttachments}. */
export interface FluxAgentAttachments {
  /** Chips in pick order, including failed ones until the user removes them. */
  attachments: PendingAttachment[];
  /** True while any upload is still running. */
  uploading: boolean;
  /** Ids of successfully uploaded files, in pick order. */
  readyIds: string[];
  /**
   * Add chips for the files and start uploading each one immediately; files over the limit become
   * error chips without a request.
   */
  addFiles: (files: Iterable<File>) => void;
  /**
   * Drop a chip and, if its upload already finished, delete the server file (best-effort). An
   * upload still in flight is not cancelled and its file stays on the server.
   */
  remove: (key: string) => void;
  /** Forget all chips after they were sent; uploaded files stay on the server. */
  clear: () => void;
}

/**
 * Manage pending uploads. `maxFileBytes` of 0 or less disables the client-side check and lets the
 * server decide.
 */
export function useFluxAgentAttachments(maxFileBytes: number): FluxAgentAttachments {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const counter = useRef(0);

  const patch = useCallback((key: string, update: Partial<PendingAttachment>) => {
    setAttachments(current =>
      current.map(item => (item.key === key ? { ...item, ...update } : item))
    );
  }, []);

  const upload = useCallback(
    async (key: string, file: File) => {
      try {
        const base64 = await blobToBase64(file);
        const result = await fluxAgentApi.uploadFile(
          file.name,
          guessMimeType(file.name, file.type),
          base64
        );
        if (result.ok) patch(key, { status: 'ready', file: result.data.file });
        else patch(key, { status: 'error', error: result.message });
      } catch {
        patch(key, { status: 'error', error: 'The file could not be read.' });
      }
    },
    [patch]
  );

  const addFiles = useCallback(
    (files: Iterable<File>) => {
      const added: PendingAttachment[] = [];
      for (const file of files) {
        const key = `attachment-${++counter.current}`;
        const tooLarge = maxFileBytes > 0 && file.size > maxFileBytes;
        added.push({
          key,
          name: file.name,
          sizeBytes: file.size,
          status: tooLarge ? 'error' : 'uploading',
          error: tooLarge ? `Larger than the ${formatBytes(maxFileBytes)} limit.` : undefined,
        });
        if (!tooLarge) void upload(key, file);
      }
      if (added.length) setAttachments(current => [...current, ...added]);
    },
    [maxFileBytes, upload]
  );

  const latest = useRef(attachments);
  latest.current = attachments;

  const remove = useCallback((key: string) => {
    // Removing a chip discards an upload the user no longer wants; the delete is best-effort.
    const target = latest.current.find(item => item.key === key);
    if (target?.file) void fluxAgentApi.deleteFile(target.file.id);
    setAttachments(current => current.filter(item => item.key !== key));
  }, []);

  const clear = useCallback(() => setAttachments([]), []);

  return {
    attachments,
    uploading: attachments.some(item => item.status === 'uploading'),
    readyIds: attachments.flatMap(item =>
      item.status === 'ready' && item.file ? [item.file.id] : []
    ),
    addFiles,
    remove,
    clear,
  };
}
